import React from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ItemDiffEntry } from '../../../utils/pattern/historyDiff';
import {
  buildJsonLineDiff,
  collapseUnchangedRows,
  LineDiffStatus,
} from '../../../utils/pattern/jsonLineDiff';
import {
  gray5,
  gray20,
  gray30,
  gray60,
  gray80,
  red50,
  green50,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import { regularSm } from '../../../constants/location';

/**
 * JSON取り込みの差分プレビュー（#3039）。
 *
 * 取り込み前後のJSONを行単位で並べ、どこが変わったかを直感的に確認できるようにする。
 * 項目IDは読み取り結果やマスタ照合から参照されるため、
 * 意図しない変更（特に項目の削除）に気づけるようにすることが目的。
 */

/** 行の状態ごとの背景色・記号 */
const rowStyle = (status: LineDiffStatus): { background: string; mark: string; color: string } => {
  if (status === 'insert') return { background: '#E6FFEC', mark: '+', color: green50 };
  if (status === 'delete') return { background: '#FFEBE9', mark: '-', color: red50 };
  return { background: 'transparent', mark: ' ', color: gray80 };
};

const styles = {
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    marginBottom: '12px',
    borderRadius: '4px',
    border: `1px solid ${red50}`,
    color: red50,
  },
  // マスタ照合・辞書変換の設定から項目が外れる旨の注意（#3344）。
  // 削除そのものの警告（赤）とは影響の重さが違うため、注意色で区別する
  mappingNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    marginBottom: '12px',
    borderRadius: '4px',
    backgroundColor: yellow_overlay10,
    color: yellow50,
  },
  mappingNoticeList: {
    ...regularSm,
    margin: '4px 0 0',
    paddingLeft: '20px',
  },
  summary: {
    display: 'flex',
    gap: '16px',
    marginBottom: '8px',
  },
  diffContainer: {
    maxHeight: '45vh',
    overflow: 'auto',
    border: `1px solid ${gray30}`,
    borderRadius: '4px',
    backgroundColor: gray5,
  },
  row: {
    display: 'flex',
    fontFamily: 'monospace',
    fontSize: '12px',
    lineHeight: '20px',
    whiteSpace: 'pre' as const,
  },
  lineNumber: {
    flexShrink: 0,
    width: '40px',
    paddingRight: '8px',
    textAlign: 'right' as const,
    color: gray60,
    userSelect: 'none' as const,
    borderRight: `1px solid ${gray30}`,
  },
  mark: {
    flexShrink: 0,
    width: '18px',
    textAlign: 'center' as const,
    userSelect: 'none' as const,
  },
  text: {
    flex: 1,
    paddingRight: '8px',
  },
  collapsed: {
    padding: '2px 12px',
    backgroundColor: gray20,
    color: gray60,
    fontSize: '11px',
    fontFamily: 'monospace',
    borderTop: `1px solid ${gray30}`,
    borderBottom: `1px solid ${gray30}`,
  },
  noChange: {
    padding: '12px',
    color: gray80,
  },
};

interface JsonImportDiffViewProps {
  /** 変更前のJSON文字列（現在の項目リスト） */
  beforeJson: string;
  /** 変更後のJSON文字列（取り込むJSON） */
  afterJson: string;
  /** 項目単位の差分。削除された項目の警告に使う */
  diffEntries: ItemDiffEntry[];
  /**
   * 取り込みで外れるマスタ照合・辞書変換の設定（#3344）。
   * 「項目名（ファイル名の参照箇所）」形式の文字列（formatItemReferencesWithItemName の戻り値）。
   * 照合キーは取り込み前に弾かれるため、ここに来るのは絞り込み・反映先・履歴分離キーのみ。
   */
  mappingNotices?: string[];
}

const JsonImportDiffView: React.FC<JsonImportDiffViewProps> = ({
  beforeJson,
  afterJson,
  diffEntries,
  mappingNotices = [],
}) => {
  const rows = React.useMemo(
    () => collapseUnchangedRows(buildJsonLineDiff(beforeJson, afterJson)),
    [beforeJson, afterJson],
  );

  // 項目の削除は既存の読み取り結果に影響するため、行の色分けとは別に文字でも警告する
  const removedItems = diffEntries.filter(e => e.status === 'removed');
  const addedCount = rows.filter(r => r.type === 'line' && r.status === 'insert').length;
  const deletedCount = rows.filter(r => r.type === 'line' && r.status === 'delete').length;
  const hasChange = addedCount > 0 || deletedCount > 0;

  return (
    <Box>
      {removedItems.length > 0 && (
        <Box sx={styles.warning}>
          <WarningAmberRoundedIcon fontSize="small" />
          <Typography sx={regularSm}>
            {removedItems.length}件の項目が削除されます（
            {removedItems.map(e => e.before?.parentValue).join('、')}
            ）。項目を削除すると、これまで読み取ったデータが表示されなくなります。同じ名称の項目を追加しても新しい項目とみなされます。
          </Typography>
        </Box>
      )}

      {/* #3344: マスタ照合・辞書変換の設定から項目が外れる旨を知らせる。
          照合キーは取り込み前に弾かれるため、ここは絞り込み・反映先・履歴分離キーのみ */}
      {mappingNotices.length > 0 && (
        <Box sx={styles.mappingNotice}>
          <WarningAmberRoundedIcon fontSize="small" />
          <Box>
            <Typography sx={regularSm}>
              反映すると、以下のマスタ照合・辞書変換の設定から項目が外れます。
            </Typography>
            <Box component="ul" sx={styles.mappingNoticeList}>
              {mappingNotices.map(notice => (
                <li key={notice}>{notice}</li>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {hasChange && (
        <Box sx={styles.summary}>
          <Typography sx={{ ...regularSm, color: green50 }}>+{addedCount} 行</Typography>
          <Typography sx={{ ...regularSm, color: red50 }}>-{deletedCount} 行</Typography>
        </Box>
      )}

      <Box sx={styles.diffContainer}>
        {!hasChange ? (
          <Typography sx={{ ...regularSm, ...styles.noChange }}>変更はありません。</Typography>
        ) : (
          rows.map((row, idx) => {
            if (row.type === 'collapsed') {
              return (
                <Box key={`collapsed-${idx}`} sx={styles.collapsed}>
                  ⋯ 変更のない {row.hiddenCount} 行
                </Box>
              );
            }
            const { background, mark, color } = rowStyle(row.status);
            return (
              <Box key={`line-${idx}`} sx={{ ...styles.row, backgroundColor: background }}>
                <Box sx={styles.lineNumber}>{row.beforeLineNumber ?? ''}</Box>
                <Box sx={styles.lineNumber}>{row.afterLineNumber ?? ''}</Box>
                <Box sx={{ ...styles.mark, color }}>{mark}</Box>
                <Box sx={{ ...styles.text, color }}>{row.text}</Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default JsonImportDiffView;
